import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApiClient } from "../../contexts/ApiClientContext";
import APIEndPoints from "../../utils/APIEndPoints";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import SETTINGS from "../../globals";
import AppSections from "../../utils/AppSections";
import AppSubSections from "../../utils/AppSubSections";
import ButtonInfo from "../common/button/ButtonInfo";
import AppIcons from "../../utils/AppIcons";
import Loading from "../common/Loading";
import ButtonType from "../common/button/ButtonType";
import { User } from "../../model/User";
import DialogWindowType from "../common/dialog_window/DialogWindowType";
import { useAlerts } from "../../contexts/AlertsContext";
import { useAuth } from "../../contexts/AuthContext";
import AlertMessageType from "../common/alert_message/AlertMessageType";
import { IApiClientError } from "../../shared/ApiClient";

function UserDetails() {
  // read the ID from the url
  const { id } = useParams();
  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // get a reference to navigation component
  const navigate = useNavigate();
  // reference to the alerts manager (add appropriate import/hook based on your project structure)
  const alertsManager = useAlerts(); // or similar hook/context getter
  const authManager = useAuth();

  // manage loading animation
  const [loading, setLoading] = useState(true);
  // set the data
  const [data, setData] = useState<User>(User.EmptyInstance());

  const [blockedRequest, setBlockedRequest] = useState<boolean>(false);
  const userMessageRef = React.useRef<string>("");

  // load record data from the server
  useEffect(() => {
    const getAllData = async () => {
      try {
        let user: User | undefined = undefined;
        if (id) {
          user = await apiClient?.get<User>({
            url: APIEndPoints.USERS.ONE(parseInt(id)),
          });
          setData(user);
        }
      } catch (error: any) {}
      setLoading(false);
    };
    getAllData();
  }, [apiClient, id]);

  if (loading) {
    return <Loading />;
  }

  const handleRequest = (requestType: string) => {
    console.log("Request sent!!!!!!!")
    console.log("Request type: ", requestType)
  
    var desired_role = "Specialist";
    if (requestType==="BOARD_REQUEST"){
      desired_role = "Board Member";
    }

    let user_upgrade_request_message = "This is an automated message generated at the request of user identified by username: " + data.username + ". The user requests approval for elevated access rights granted by the role of: " + desired_role + "."; 

    user_upgrade_request_message += "\n\n Attached below is the user's provided justification: \n\n "
    user_upgrade_request_message += userMessageRef.current;
    console.log("User message: ", user_upgrade_request_message)

    const body = {"category": "board_member",
      "message": user_upgrade_request_message }
    apiClient
        ?.post({
          url: APIEndPoints.EMAILS.NEW,
          body: body,
        })
        .then((response) => {
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "User updated successfully",
          });
        })
        .catch((error: IApiClientError) => {
          alertsManager.showAlertMessage({
            type: AlertMessageType.ERROR,
            message: "Failed to send request!",
          });
        });

  }

  const renderRequestSection = (labelText: string, dialogMessage: string, requestType: string) => (
    <div className="form-input-group mt-4 flex flex-col gap-2 pl-4">
      <label className="text-sm">
        {labelText}
      </label>

      <label className="flex items-center gap-2 text-sm mt-1">
        <span>
          <Button
            label={"Request"}
            purpose={ButtonAction.INFO}
            isSubmit={false}
            onClick={() => {
              alertsManager.showDialogWindow({
                type: DialogWindowType.CONTACTFORM,
                message: dialogMessage,
                cancelLabel: "Cancel",
                confirmLabel: "Yes, submit!",
                onConfirm: () => handleRequest(requestType),
                onUserMessageChange: (msg) => { userMessageRef.current = msg; },
              });
            }}
            className="w-full flex justify-start text-left"
          />
        </span>
      </label>
    </div>
  );
  

  return (
    <div className="details-page">
      <div className="details-header">
        {/* Info Section */}
        <div className="details-header-content">
          <div className="details-title">
            <h1>{data.name + " " + data.surname}</h1>
          </div>
          <div className="details-overview">
            <ButtonInfo className="span-3">
              {AppIcons.USER}
              Username: <span>{data.username}</span>
            </ButtonInfo>
            <ButtonInfo className="span-3">
              {AppIcons.EMAIL}
              Email: <span>{data.email}</span>
            </ButtonInfo>
          </div>
        </div>
      </div>

      {/* Details Section */}
      <div className="details-section">
        <div className="details-section-title">
          <h2>User details</h2>
          <Button
            label="Edit information"
            purpose={ButtonAction.EDIT}
            type={ButtonType.ICON_TEXT}
            onClick={() => {
              navigate(
                SETTINGS.get_path({
                  section: AppSections.USERS,
                  id: data.id,
                  subsection: AppSubSections.EDIT,
                }),
              );
            }}
          />
        </div>

        <p className="details-description">
          Name: {data.name ?? "No description provided."}
        </p>
        <p className="details-description">
          Surname: {data.surname ?? "No description provided."}
        </p>
        <p className="details-description">
          Profession: {data.profession ?? "No description provided."}
        </p>
        <p className="details-description">
          Affiliation: {data.affiliation ?? "No description provided."}
        </p>
        <p className="details-description">
          Active since: {data.activity_start ?? "No description provided."}
        </p>
        <p className="details-description">
          Role: {data.role ?? "No description provided."}
        </p>

      {Boolean(data.board_member) && (
        <p className="details-description">
          Board member: Yes
        </p>
      )}
       
      </div>

      {(authManager.isSpecialist() && !authManager.isBoardMember()) &&
        renderRequestSection(
          "Submit a formal request to existing board members to join the Board",
          "This will prompt a message being sent to each existing board member to review your formal application for your inclusion into to board. Are you sure you want to submit this formal request?",
          "BOARD_REQUEST"
        )
      }

      {(authManager.isVisitor()) &&
        renderRequestSection(
          "Submit a formal request to be granted specialist role",
          "This will prompt a message being sent to each existing board member to review your formal application for you being granted specialist role. This will grant you the right to contribute data to the repository. Are you sure you want to submit this formal request?",
          "SPECIALIST_REQUEST"
        )
      }

    </div>
  );
}

export default UserDetails;
